<div class="payment-selector-compact">
  <!-- Customer Required Message -->
  <div *ngIf="!formGroup.controls.customerId.value" class="info-message">
    <i class="pi pi-info-circle"></i>
    <span>Select a customer first to load payment methods</span>
  </div>

  <ng-container *ngIf="formGroup.controls.customerId.value">
    <!-- Loading State -->
    <div *ngIf="isLoading" class="loading-inline">
      <p-skeleton width="100%" height="2.5rem" borderRadius="0.5rem" />
      <p-skeleton
        width="100%"
        height="2.5rem"
        borderRadius="0.5rem"
        styleClass="mt-3"
      />
    </div>

    <!-- Accordion for Payment Method Selection -->
    <p-accordion
      *ngIf="!isLoading"
      [multiple]="true"
      [(activeIndex)]="paymentAccordionActiveIndex"
      styleClass="compact-accordion"
    >
      <!-- Select Payment Method Panel -->
      <p-accordionpanel value="0">
        <p-accordionheader>
          <div class="accordion-header-content">
            <i class="pi pi-credit-card"></i>
            <span>Select Payment Method</span>
            @if (
              !formGroup.controls.paymentMethodId.value &&
              !isNewPaymentMethodValid
            ) {
              <span class="required-badge">Required</span>
            }
          </div>
        </p-accordionheader>
        <p-accordioncontent>
          <div class="accordion-section-content">
            <!-- Currency -->
            <div class="field-group">
              <label for="currency" class="field-label">
                Currency<span *ngIf="required" class="required">*</span>
              </label>
              <p-select
                inputId="currency"
                [formControl]="formGroup.controls.currencyIso"
                [options]="currencies"
                (onChange)="onCurrencyChange($event)"
                [disabled]="disabled"
                [filter]="true"
                placeholder="Select currency"
                styleClass="w-full"
                appendTo="body"
              />
            </div>

            <!-- Payment Methods Select with Filter -->
            <div class="field-group mt-4">
              <label for="payment-method-select" class="field-label">
                Select Payment Method<span *ngIf="required" class="required"
                  >*</span
                >
              </label>
              <p-select
                inputId="payment-method-select"
                [formControl]="formGroup.controls.paymentMethodId"
                (onChange)="onPaymentMethodSelectChange($event)"
                [options]="paymentMethodOptions"
                [filter]="true"
                [disabled]="disabled"
                placeholder="Choose a payment method"
                styleClass="w-full payment-method-select"
                appendTo="body"
                optionLabel="label"
                optionValue="value"
                [showClear]="false"
              >
                <!-- Custom template for selected item -->
                <ng-template pTemplate="selectedItem" let-selectedOption>
                  <div class="selected-payment-method" *ngIf="selectedOption">
                    <i
                      [class]="
                        selectedOption.value === null
                          ? 'pi pi-plus'
                          : 'pi pi-credit-card'
                      "
                    ></i>
                    <div class="pm-info">
                      <div class="pm-type">
                        {{ selectedOption.data?.Type || selectedOption.label }}
                      </div>
                      <!-- Show more info for selected item -->
                      <div
                        class="pm-selected-details"
                        *ngIf="selectedOption.data"
                      >
                        <span
                          *ngIf="selectedOption.data.Description"
                          class="pm-desc-chip"
                        >
                          {{ selectedOption.data.Description }}
                        </span>
                        <span
                          *ngIf="
                            selectedOption.data.GatewayCustomerId &&
                            !selectedOption.data.Description
                          "
                          class="pm-id-chip"
                        >
                          {{ selectedOption.data.GatewayCustomerId }}
                        </span>
                      </div>
                    </div>
                  </div>
                </ng-template>

                <!-- Custom template for dropdown items -->
                <ng-template pTemplate="item" let-option>
                  <div
                    class="payment-method-option"
                    [class.add-new-option]="option.value === null"
                    [class.selected-option]="
                      option.value ===
                        formGroup.controls.paymentMethodId.value &&
                      option.value !== null
                    "
                  >
                    <div class="pm-option-content">
                      <i
                        [class]="
                          option.value === null
                            ? 'pi pi-plus'
                            : 'pi pi-credit-card'
                        "
                      ></i>
                      <div class="pm-info">
                        <div class="pm-type-line">
                          <span class="pm-type">{{
                            option.data?.Type || option.label
                          }}</span>
                        </div>
                        <div class="pm-details" *ngIf="option.data">
                          <div
                            class="pm-detail-line"
                            *ngIf="option.data.Description"
                          >
                            <span class="pm-label">Description:</span>
                            <span class="pm-value">{{
                              option.data.Description
                            }}</span>
                          </div>
                          <div
                            class="pm-detail-line"
                            *ngIf="option.data.GatewayCustomerId"
                          >
                            <span class="pm-label">Gateway Customer ID:</span>
                            <span class="pm-value"
                              >({{ option.data.GatewayCustomerId }})</span
                            >
                          </div>
                          <div
                            class="pm-detail-line"
                            *ngIf="option.data.GatewayPaymentId"
                          >
                            <span class="pm-label">Gateway Payment ID:</span>
                            <span class="pm-value"
                              >({{ option.data.GatewayPaymentId }})</span
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- Selection indicator -->
                    <i
                      *ngIf="
                        option.value ===
                          formGroup.controls.paymentMethodId.value &&
                        option.value !== null
                      "
                      class="pi pi-check pm-selected-indicator"
                    ></i>
                  </div>
                </ng-template>
              </p-select>

              <!-- Validation - only show error when no existing method selected AND new method form is not valid -->
              <small
                *ngIf="
                  required &&
                  !formGroup.controls.paymentMethodId.value &&
                  !isNewPaymentMethodValid
                "
                class="error-text"
              >
                Payment method is required
              </small>
            </div>
          </div>
        </p-accordioncontent>
      </p-accordionpanel>

      <!-- New Payment Method Form Panel (Only show when creating new) -->
      <p-accordionpanel value="1" *ngIf="isCreatingNewPaymentMethod()">
        <p-accordionheader>
          <div class="accordion-header-content">
            <i class="pi pi-plus-circle"></i>
            <span>New Payment Method</span>
          </div>
        </p-accordionheader>
        <p-accordioncontent>
          <div class="accordion-section-content">
            <app-payment-method-form
              [siteId]="siteId"
              [disabled]="disabled"
              [paymentMethod]="newPaymentMethod"
              [accessToken]="accessToken"
              (paymentMethodChange)="onNewPaymentMethodChange($event)"
              (validationChange)="onNewPaymentMethodValidationChange($event)"
            />
          </div>
        </p-accordioncontent>
      </p-accordionpanel>

      <!-- Load Methods from Gateway Panel -->
      <!-- Hidden for Shopify payment methods - matches old QMC line 807 -->
      <p-accordionpanel value="2" *ngIf="!isShopifyPaymentMethod()">
        <p-accordionheader>
          <div class="accordion-header-content">
            <i class="pi pi-download"></i>
            <span>Load Methods from Gateway</span>
          </div>
        </p-accordionheader>
        <p-accordioncontent>
          <div class="accordion-section-content">
            <!-- Gateway Customer Profile Selector -->
            <div class="field-group">
              <label for="gateway-profile-select" class="field-label">
                Gateway Customer Profile
              </label>
              <p-select
                inputId="gateway-profile-select"
                [(ngModel)]="paymentProfileToLoad"
                (onChange)="onPaymentProfileChange($event)"
                [options]="paymentProfiles"
                [disabled]="disabled || isLoadingGatewayMethods"
                placeholder="Select gateway customer profile"
                styleClass="w-full"
                appendTo="body"
              >
                <!-- Custom template for dropdown items -->
                <ng-template pTemplate="item" let-profile>
                  <div class="gateway-profile-option">
                    <span class="profile-type">{{
                      getPaymentTypeDisplayName(profile.Type)
                    }}</span>
                    <span class="profile-id" *ngIf="profile.GatewayCustomerId">
                      ({{ profile.GatewayCustomerId }})
                    </span>
                  </div>
                </ng-template>

                <!-- Custom template for selected item -->
                <ng-template pTemplate="selectedItem" let-profile>
                  <div class="gateway-profile-selected" *ngIf="profile">
                    <span class="profile-type">{{
                      getPaymentTypeDisplayName(profile.Type)
                    }}</span>
                    <span class="profile-id" *ngIf="profile.GatewayCustomerId">
                      ({{ profile.GatewayCustomerId }})
                    </span>
                  </div>
                </ng-template>
              </p-select>
            </div>

            <!-- Gateway Customer Profile ID Input -->
            <div class="field-group mt-4" *ngIf="paymentProfileToLoad">
              <label for="gateway-profile-id" class="field-label">
                Gateway Customer Profile ID
              </label>
              <input
                pInputText
                id="gateway-profile-id"
                type="text"
                [ngModel]="paymentProfileToLoad!.GatewayCustomerId || ''"
                (ngModelChange)="onGatewayProfileIdChange($event)"
                [disabled]="
                  disabled ||
                  isLoadingGatewayMethods ||
                  paymentProfileToLoad!.Id !== 0
                "
                placeholder="Enter gateway customer profile ID"
                class="w-full"
              />
            </div>

            <!-- Load Profile Button -->
            <div
              class="field-group mt-4"
              *ngIf="paymentProfileToLoad && paymentProfileToLoad.Id === 0"
            >
              <p-button
                label="Load profile"
                icon="pi pi-download"
                severity="success"
                (onClick)="loadProfile()"
                [disabled]="
                  disabled ||
                  isLoadingGatewayMethods ||
                  !paymentProfileToLoad!.GatewayCustomerId
                "
                [loading]="isLoadingGatewayMethods"
                styleClass="w-full"
              />
            </div>

            <!-- Loading Skeleton for External Methods -->
            <div
              class="external-methods-section mt-4"
              *ngIf="isLoadingGatewayMethods || isSelectingMethod"
            >
              <h4 class="section-title">Available Payment Methods</h4>
              <div class="payment-method-cards">
                <div class="payment-method-card">
                  <div class="payment-method-card-content">
                    <p-skeleton
                      width="100%"
                      height="1.5rem"
                      styleClass="mb-2"
                    />
                    <p-skeleton width="80%" height="1.5rem" styleClass="mb-2" />
                    <p-skeleton width="60%" height="1.5rem" styleClass="mb-2" />
                  </div>
                  <div class="payment-method-card-actions">
                    <p-skeleton width="100%" height="2.5rem" />
                  </div>
                </div>
              </div>
            </div>

            <!-- External Payment Methods Cards -->
            <div
              class="external-methods-section mt-4"
              *ngIf="
                !isLoadingGatewayMethods &&
                !isSelectingMethod &&
                externalMethods &&
                externalMethods.length > 0
              "
            >
              <h4 class="section-title">Available Payment Methods</h4>
              <div class="payment-method-cards">
                <div
                  *ngFor="let method of externalMethods"
                  class="payment-method-card"
                  [class.selected]="
                    method.Id > 0 &&
                    method.Id === formGroup.controls.paymentMethodId.value
                  "
                >
                  <div class="payment-method-card-content">
                    <dl class="payment-method-details">
                      <dt>Gateway Payment ID</dt>
                      <dd>{{ method.GatewayPaymentId || "not present" }}</dd>

                      <dt *ngIf="method.BillingFirstName">
                        Billing First Name
                      </dt>
                      <dd *ngIf="method.BillingFirstName">
                        {{ method.BillingFirstName }}
                      </dd>

                      <dt>Billing Last Name</dt>
                      <dd>{{ method.BillingLastName }}</dd>

                      <dt>Description</dt>
                      <dd>{{ method.Description }}</dd>
                    </dl>
                  </div>
                  <div class="payment-method-card-actions">
                    <p-button
                      *ngIf="
                        !method.Id ||
                        method.Id !== formGroup.controls.paymentMethodId.value
                      "
                      label="Select"
                      (onClick)="chooseMethod(method)"
                      [disabled]="disabled || isSelectingMethod"
                      [loading]="isSelectingMethod"
                      severity="secondary"
                      styleClass="w-full"
                    />
                    <div
                      *ngIf="
                        method.Id &&
                        method.Id === formGroup.controls.paymentMethodId.value
                      "
                      class="selected-badge"
                    >
                      <i class="pi pi-check"></i>
                      <span>Selected</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </p-accordioncontent>
      </p-accordionpanel>
    </p-accordion>
  </ng-container>
</div>
